import { useMemo, useRef } from 'react';
import qrcode from 'qrcode-generator';
import { UI, type Lang } from '@ringring/core';
import { Icon } from './Icon';

/** Four clear modules on every side; square modules retain their orientation in RTL. */
function qrDrawing(url: string): { size: number; path: string } {
  const qr = qrcode(0, 'M');
  qr.addData(url);
  qr.make();
  const modules = qr.getModuleCount();
  const paths: string[] = [];
  for (let row = 0; row < modules; row++) {
    for (let col = 0; col < modules; col++) {
      if (qr.isDark(row, col)) paths.push(`M${col + 4} ${row + 4}h1v1h-1z`);
    }
  }
  return { size: modules + 8, path: paths.join('') };
}

export function ReviewQr({ lang, url }: { lang: Lang; url: string }) {
  const t = UI[lang];
  const svgRef = useRef<SVGSVGElement>(null);
  const drawing = useMemo(() => qrDrawing(url), [url]);

  const download = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const copy = svg.cloneNode(true) as SVGSVGElement;
    // Resolve the app's exact brand tokens so the downloaded SVG works outside the app.
    for (const tag of ['rect', 'path']) {
      const source = svg.querySelector(tag);
      if (source) copy.querySelector(tag)?.setAttribute('fill', getComputedStyle(source).fill);
    }
    copy.removeAttribute('class');
    const blob = new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'ringring-google-review.svg';
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  return (
    <details className="review-qr disclosure-panel">
      <summary><Icon name="qr" />{t.review_qr_title}<Icon name="plus" /></summary>
      <div className="review-qr-content">
        <svg ref={svgRef} className="review-qr-code" xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${drawing.size} ${drawing.size}`} width={drawing.size * 8} height={drawing.size * 8} role="img" aria-label={t.review_qr_title} shapeRendering="crispEdges">
          <title>{t.review_qr_title}</title>
          <rect width={drawing.size} height={drawing.size} fill="var(--surface)" />
          <path d={drawing.path} fill="var(--text)" />
        </svg>
        <div className="review-qr-copy">
          <h2>{t.review_qr_heading}</h2>
          <p>{t.review_qr_hint}</p>
          <div className="row">
            <button className="btn" type="button" onClick={download}><Icon name="download" />{t.qr_download}</button>
            <a className="btn secondary" href={url} target="_blank" rel="noreferrer"><Icon name="external" />{t.review_qr_open}</a>
          </div>
          <a className="review-qr-url" href={url} target="_blank" rel="noreferrer"><bdi dir="ltr">{url}</bdi></a>
        </div>
      </div>
    </details>
  );
}
